import moment from "moment";

import {
  TenancyCustomFieldsDocument,
  UpdateTenancyCustomFieldDocument,
  CreateTenancyCustomFieldDocument,
  DeleteTenancyCustomFieldDocument,
  AssetManagementCustomFieldType,
  AssetManagementTenancyFlagValue,
  type AssetManagementTenancy,
  AssetManagementSaleStatus,
} from "~/graphql/generated/graphql";
import { getBrokerMandate, type Valuation, getLatestSalesFinances, getDaysOnMarket, type SalesFinance } from "~/helpers/modules/sales-helpers";

import type { InjectionKey, Ref } from "vue";
import type { DataTableRowSaveFunction, TableContext, TableHeaderConfig } from "~/helpers/datatable-helpers";
import { useApolloClient, type MutateOverrideOptions } from "@vue/apollo-composable";
import { hasValue, filterWithValue, type TranslationKey, groupBy, sum, convertArea } from "~/helpers/common-helpers";
import type { CustomField } from "~/helpers/modules/data-mapping-helpers";
import { getPortfolioColorForeground, getPortfolioColorBackground } from "~/helpers/entities/portfolio-helpers";

import {
  type Property,
  type PropertyFlag,
  type BusinessPlan,
  type FinancialRecordsEntity,
  type BusinessPlansEntity,
  type Location,
} from "~/helpers/entities/property-helpers";
import { type Tenant, type RentMetric, isTenantCurrent } from "~/helpers/entities/tenant-helpers";
import { filterDisabledColumns, isColumnDisabledInDev, type TenancyStatus } from "~/helpers/modules/rent-roll-helpers";
import { getGroupedColumns, type ColumnGroup } from "~/helpers/column-settings-helpers";

export type TenancyCustomField = { customField?: CustomField; value?: Nullable<string> };

export type ExternalId = { externalID: string; externalIDType: string };

export type Responsible = { id: string; name?: Nullable<string> };

export type TenancyFlag = { id: string; value: AssetManagementTenancyFlagValue };

export type ExternalEntity = { assetManagementExternalIDs?: Nullable<{ items: ExternalId[] }> };

export type SalesEntity = { assetManagementSales?: Nullable<{ items: SalesFinance[] }> };

export type CustomFieldsEntity = { assetManagementCustomFields?: TenancyCustomField[] };

export type ResponsiblesEntity = { assetManagementResponsibles?: Responsible[] };

export type FlagsEntity<F extends TenancyFlag | PropertyFlag> = { flags?: F[] };

export type TenantEntity = {
  assetManagementTenantAt?: Nullable<Tenant>;
  assetManagementTenants?: {
    items?: Nullable<Tenant[]>;
  };
};

export type ValuationsEntity = {
  assetManagementValuations?: Nullable<{ items?: Nullable<Valuation[]> }>;
};

export type PlannedRent = { startDate: string; amount: number };
export type Tenancy = {
  id: string;
  plannedRent?: Nullable<RentMetric>;
  latestPlannedRent?: Nullable<RentMetric>;
  area?: Nullable<number>;
  areaUnit?: Nullable<string>;
  tenancyType?: Nullable<string>;
  rentRegulationPrinciple?: Nullable<string>;
  rooms?: Nullable<number>;
  status?: Nullable<AssetManagementSaleStatus>;
  startDate?: Nullable<string>;
  endDate?: Nullable<string>;
  assetMamangementTenants?: Nullable<Tenant[]>;
  assetManagementPlannedRents?: Nullable<{ items: PlannedRent[] }>;
  assetManagementTenantAt?: Nullable<Tenant>;
  assetManagementBusinessPlans?: Nullable<{ items: BusinessPlan[] }>;
  assetManagementProperty?: Nullable<Property>;
} & FinancialRecordsEntity &
  ValuationsEntity &
  ExternalEntity &
  SalesEntity &
  CustomFieldsEntity &
  ResponsiblesEntity &
  TenantEntity &
  FlagsEntity<TenancyFlag> &
  Location;

declare const t: AssetManagementTenancy;

export type IdleTenancy = {
  period: any;
  occupiedDays: number;
  possibleDays: number;
  lostRent?: number | null;
  possibleRent?: number | null;
  possibleDaysWithRent: number;
  occupiedDaysWithRent: number;
};

export type LeaseMetrics = {
  totalTenancies: number;
  period: any;
  evictions: number;
};

export type PlannedRentMetrics = {
  period: any;
  amount: number;
  currency: string;
};

export type TenanciesDate = string | "today";

export const getTenancyAddress = (location: Location) => {
  let address = location.name || "";

  if (location.zipCode) {
    address += `, ${location.zipCode}`;
  }

  if (location.city) {
    address += ` ${location.city}`;
  }

  return address;
};

export const getRentCurrency = (tenancy: Nullable<Tenancy>) => (tenancy?.plannedRent)?.currency;

export const getYearlyRent = (tenancy: Nullable<Tenancy>) => (tenancy?.plannedRent)?.amount || 0;

export const getMonthlyRent = (tenancy: Tenancy) => getYearlyRent(tenancy) / 12;

export const getMonthlyRentPerArea = (tenancy: Tenancy) => (tenancy.area ? getMonthlyRent(tenancy) / tenancy.area : null);

export const getRentPerAreaPerAnnum = (tenancy: Tenancy) => (tenancy.area ? getYearlyRent(tenancy) / tenancy.area : null);

export const getRentPrDate = (tenancy: Tenancy, startDate: string, endDate: string) => {
  const rents = tenancy.assetManagementPlannedRents?.items.filter((item) => {
    return endDate ? moment(item.startDate).isBetween(startDate, endDate, null, "[]") : moment(item.startDate).isAfter(startDate);
  });

  const mostRecentRent = rents?.reduce((acc, item) => {
    return item.startDate > acc.startDate ? item : acc;
  });

  return mostRecentRent?.amount || 0;
};

export const getCurrentTenant = (tenancy: Tenancy) => {
  return tenancy?.assetManagementTenantAt;
};

export const getUpcomingTenant = (tenancy: TenantEntity) => {
  const fromDate = tenancy.assetManagementTenantAt?.evictionDate ? moment.utc(tenancy.assetManagementTenantAt?.evictionDate) : moment.utc();

  return tenancy.assetManagementTenants?.items?.find((t) => moment.utc(t.moveInDate).isAfter(fromDate));
};

export const getCurrentTenantBalance = (tenancy: TenantEntity) => {
  const tenant = tenancy?.assetManagementTenantAt;

  return tenant?.balanceAt ?? tenant?.latestBalance;
};

export const getLeaseStart = (tenancy: Tenancy, tenant?: Nullable<Tenant>) => {
  tenant ??= getCurrentTenant(tenancy);

  return tenant?.moveInDate;
};

export const getLeaseEnd = (tenancy: Tenancy, tenant?: Nullable<Tenant>) => {
  tenant ??= getCurrentTenant(tenancy);

  return tenant?.evictionDate;
};

export const getExternalIdForTenancy = (tenancy: ExternalEntity) => {
  const externalId = tenancy?.assetManagementExternalIDs?.items.find((item) => {
    return item.externalIDType == "id";
  });

  return externalId?.externalID ?? null;
};

export const isVacant = (tenancy: Tenancy) => {
  return getCurrentTenant(tenancy) == null;
};

export const isTerminated = (tenancy: { id: string; assetManagementTenantAt?: Nullable<Tenant> }) => {
  const currentTenant = getCurrentTenant(tenancy);

  return moment(currentTenant?.evictionDate).isValid();
};

export const getIdleDate = (tenancy: Tenancy): Nullable<string> => {
  if (!isVacant(tenancy)) {
    return null;
  }

  const allTenants = tenancy.assetManagementTenants?.items?.slice();

  const previousTenants = allTenants
    ?.filter((tenant) => tenant.evictionDate && moment(tenant.evictionDate).isBefore(moment()))
    .sort((a, b) => moment(b.evictionDate).diff(moment(a.evictionDate)));

  if (!previousTenants?.length) {
    return tenancy.startDate ?? tenancy.assetManagementProperty?.administrationStartDate ?? null;
  }

  return previousTenants[0].evictionDate;
};

export const getIdleDays = (tenancy: Tenancy): Nullable<number> => {
  const idleDate = getIdleDate(tenancy);

  if (!idleDate) {
    return null;
  }

  return moment().diff(moment(idleDate), "days");
};

export const getAskingPrice = (tenancy: Nullable<ValuationsEntity>) => {
  if (!hasValue(tenancy?.assetManagementValuations?.items) || tenancy.assetManagementValuations.items.length == 0) {
    return undefined;
  }

  const selectedValuation = tenancy.assetManagementValuations.items.find((item) => item.isAgentSelected) || tenancy.assetManagementValuations.items[0];

  return selectedValuation?.askingPrice;
};

export const getActualSalesPrice = (tenancy: Nullable<SalesEntity>) => {
  if (!hasValue(tenancy?.assetManagementSales?.items) || tenancy.assetManagementSales.items.length == 0) {
    return undefined;
  }

  return getLatestSalesFinances(tenancy?.assetManagementSales?.items)?.actualSalesPrice;
};

export const getTenancyDaysOnMarket = (tenancy: SalesEntity) => {
  const latestSales = getLatestSalesFinances(tenancy?.assetManagementSales?.items);

  if (!latestSales) {
    return null;
  }

  return getDaysOnMarket(latestSales);
};

export const getTenancyBrokerMandate = (tenancy: Tenancy) => {
  const valuations = tenancy.assetManagementValuations?.items;

  if (valuations == null || valuations.length == 0) {
    return 0;
  }

  return getBrokerMandate(valuations);
};

export const getTenancyPortfolioColorForeground = (tenancy: Tenancy) => getPortfolioColorForeground(tenancy.assetManagementProperty?.assetManagementPortfolio);

export const getTenancyPortfolioColorBackground = (tenancy: Tenancy) => getPortfolioColorBackground(tenancy.assetManagementProperty?.assetManagementPortfolio);

export const getBusinessPlanForTenancy = (tenancy: Nullable<BusinessPlansEntity>) => {
  // Note (Morten): Pick the latest updated business plan
  if (!hasValue(tenancy?.assetManagementBusinessPlans?.items) || tenancy.assetManagementBusinessPlans?.items.length == 0) {
    return null;
  }

  return [...tenancy.assetManagementBusinessPlans.items].sort((a, b) => {
    return a?.updatedAt && b?.updatedAt ? moment(b.updatedAt).diff(moment(a.updatedAt)) : 0;
  })[0];
};

export const getSortedTenancyHeaders = (headers: TableHeaderConfig<Tenancy, TenanciesColumnKey>[], preferencesColumns: TenanciesColumnKey[]) =>
  filterWithValue(preferencesColumns.map((column) => headers.find((header): boolean => header.id === column)));

export const parseCustomFieldKey = (key: TenanciesCustomFieldKey): { id: string; name: string } => {
  const [, id, name] = key.split("_");

  return { id, name };
};

export const stringifyCustomField = ({ id, name }: { id: string; name: string }) => `custom_${id}_${name}` as TenanciesCustomFieldKey;

export const getRentRegulationPrinciple = (tenancy: Nullable<Tenancy>) => {
  return tenancy?.rentRegulationPrinciple;
};

export const tableHeaderRender = <T>(header: TableHeaderConfig<T>, row: T, tableContext: TableContext<T>) => {
  const filter = header.dataTableCellOptions?.filter;
  const value = header.value(row);
  return filter ? filter(value, row, tableContext) : value;
};

export const getOnSaveTenanciesCustomTextField = (field: CustomField) => {
  return (async (rowValue: Nullable<string | number>, row: Tenancy) => {
    const customFieldId = field.id;

    const value = field.fieldType === AssetManagementCustomFieldType.Boolean ? Boolean(rowValue) : rowValue?.toString();

    const { client } = useApolloClient();

    /**Note Sven: We avoid refetch to not lose current row order in the table after editing (confusing as a user)
     * This means we may have a state where the tenancyCustomField was previously deleted without the client knowing
     * We fetch the current ones here, to see if it already exists;
     */
    const customFieldsQuery = await client.query({ query: TenancyCustomFieldsDocument, variables: { tenancyId: row.id }, fetchPolicy: "no-cache" });

    const tenancyCustomField = customFieldsQuery.data.assetManagementTenancy?.assetManagementCustomFields.find(
      (field) => field.customField.id === customFieldId
    );
    const tenancyFieldId = tenancyCustomField?.id;
    const options: MutateOverrideOptions<any> = { refetchQueries: [], awaitRefetchQueries: true };

    let newValue: string | number | undefined = undefined;

    if (!!value) {
      const update = !!tenancyFieldId;

      let res = update
        ? await client.mutate({
            ...options,
            mutation: UpdateTenancyCustomFieldDocument,
            variables: {
              input: {
                id: tenancyFieldId,
                value,
              },
            },
          })
        : await client.mutate({
            ...options,
            mutation: CreateTenancyCustomFieldDocument,
            variables: {
              input: {
                customFieldId,
                tenancyId: row.id,
                value,
              },
            },
          });

      newValue = res?.data?.result.value;
    } else if (tenancyFieldId) {
      await client.mutate({ ...options, mutation: DeleteTenancyCustomFieldDocument, variables: { id: tenancyFieldId } });
    }

    return field.fieldType === AssetManagementCustomFieldType.Boolean ? Boolean(newValue) : newValue;
  }) as DataTableRowSaveFunction<any, Tenancy, TenanciesColumnKey>;
};

export const TenanciesUserPreferenceColumns = [
  {
    key: "name",
    enabled: true,
  },
  {
    key: "tenantName",
    enabled: true,
  },
  {
    key: "unitNumber",
    enabled: true,
  },
  {
    key: "usage",
    enabled: true,
  },
  {
    key: "numberOfRooms",
    enabled: true,
  },
  {
    key: "area",
    enabled: true,
  },
  {
    key: "salesStatus",
    enabled: false,
  },
  {
    key: "takeoverDate",
    enabled: false,
  },

  {
    key: "monthlyRent",
    enabled: false,
  },

  {
    key: "monthlyRentPsm",
    enabled: false,
  },

  {
    key: "annualRent",
    enabled: true,
  },

  {
    key: "rentPsm",
    enabled: true,
  },
  {
    key: "leaseStart",
    enabled: true,
  },

  {
    key: "leaseEnd",
    enabled: true,
  },

  {
    key: "adminStart",
    enabled: false,
  },

  {
    key: "adminEnd",
    enabled: false,
  },

  {
    key: "propertyAdminStart",
    enabled: false,
  },

  {
    key: "propertyAdminEnd",
    enabled: false,
  },

  {
    key: "idleDate",
    enabled: false,
  },

  {
    key: "idleDays",
    enabled: false,
  },

  {
    key: "daysOnMarket",
    enabled: false,
  },
  {
    key: "brokerMandate",
    enabled: false,
  },

  {
    key: "zip",
    enabled: false,
  },

  {
    key: "portfolio",
    enabled: false,
  },
  {
    key: "arrears",
    enabled: true,
  },

  {
    key: "irrevocableUntilLandlord",
    enabled: true,
  },

  {
    key: "irrevocableUntilTenant",
    enabled: true,
  },

  {
    key: "leaseLength",
    enabled: false,
  },

  {
    key: "rentRegulationPrinciple",
    enabled: true,
  },
  {
    key: "property",
    enabled: false,
  },
  {
    key: "assignedTo",
    enabled: false,
  },
  {
    key: "tenantStatus",
    enabled: false,
  },
  {
    key: "tenancyStatus",
    enabled: false,
  },
] as const;

export type TenanciesCustomFieldKey = `custom_${string}_${string}`;
export type TenanciesColumnGroupKey = (typeof TenanciesUserPreferenceColumnGroups)[number]["name"] | "custom";
export type TenanciesColumnKey = (typeof TenanciesUserPreferenceColumns)[number]["key"] | TenanciesCustomFieldKey;

export const TenanciesUserPreferenceColumnGroups = [
  {
    name: "unit",
    columns: [
      "name",
      "tenancyStatus",
      "unitNumber",
      "area",
      "numberOfRooms",
      "usage",
      "adminStart",
      "adminEnd",
      "propertyAdminStart",
      "propertyAdminEnd",
      "rentRegulationPrinciple",
      "property",
      "portfolio",
      "zip",
      "assignedTo",
    ],
  },
  {
    name: "tenant",
    columns: [
      "tenantName",
      "tenantStatus",
      "monthlyRent",
      "monthlyRentPsm",
      "annualRent",
      "rentPsm",
      "leaseStart",
      "leaseEnd",
      "leaseLength",
      "idleDate",
      "idleDays",
      "arrears",
      "irrevocableUntilTenant",
      "irrevocableUntilLandlord",
    ],
  },
  {
    name: "sales",
    columns: ["salesStatus", "brokerMandate", "daysOnMarket", "takeoverDate"],
  },
] as const satisfies { name: string; columns: TenanciesColumnKey[] }[];

/**
 * Get the default user preference columns with disabled columns filtered out
 */
export const getFilteredDefaultColumns = (): TenanciesColumnKey[] => {
  return TenanciesUserPreferenceColumns.filter((column) => column.enabled && !isColumnDisabledInDev(column.key)).map((column) => column.key);
};

export const getTenancyStatus = (row: Tenancy): TenancyStatus => {
  return !hasValue(row.assetManagementTenantAt)
    ? "vacant"
    : moment.utc(row.assetManagementTenantAt.evictionDate).isValid()
    ? "occupiedTerminated"
    : "occupiedNotTerminated";
};

export const hasChurn = computed(() => (tenancy: Tenancy) => {
  if (!tenancy) return false;

  const currentTenant = getCurrentTenant(tenancy);

  return currentTenant == null;
});

export const getCurrentRent = (tenancy: Tenancy) => {
  if (!tenancy) return { amount: 0, currency: undefined };

  const rent = tenancy.plannedRent ?? tenancy.latestPlannedRent;
  const amount = rent?.amount;
  const currency = rent?.currency ?? undefined;

  return { amount, currency };
};

/**
 * Map of tenancy type string to translation key
 */
export const TENANCY_TYPE_TRANSLATION_KEYS: Record<string, TranslationKey> = {
  UNKNOWN: "TENANCY_TYPE_UNKNOWN",
  OFFICE: "TENANCY_TYPE_OFFICE",
  LOGISTICS_INDUSTRIAL: "TENANCY_TYPE_LOGISTICS_INDUSTRIAL",
  RETAIL: "TENANCY_TYPE_RETAIL",
  RESIDENTIAL: "TENANCY_TYPE_RESIDENTIAL",
  PUBLIC: "TENANCY_TYPE_PUBLIC",
  HOTEL: "TENANCY_TYPE_HOTEL",
  PARKING: "TENANCY_TYPE_PARKING",
  GARAGE: "TENANCY_TYPE_GARAGE",
  COMMERCIAL: "TENANCY_TYPE_COMMERCIAL",
  OTHER: "TENANCY_TYPE_OTHER",
} as const;

export const getTenancyTypeTranslationKey = (tenancyType?: Nullable<string>): TranslationKey =>
  TENANCY_TYPE_TRANSLATION_KEYS[tenancyType ?? "UNKNOWN"] ?? "TENANCY_TYPE_UNKNOWN";

export const RENT_REGULATION_TRANSLATION_KEYS: Record<string, TranslationKey> = {
  UNKNOWN: "RENT_REGULATION_UNKNOWN",
  COST_BASED: "RENT_REGULATION_COST_BASED",
  VALUE_BASED: "RENT_REGULATION_VALUE_BASED",
  FREE_RENT_SETTING: "RENT_REGULATION_FREE_RENT_SETTING",
  SMALL_HOUSES: "RENT_REGULATION_SMALL_HOUSES",
  IMPROVED_VALUE_BASED: "RENT_REGULATION_IMPROVED_VALUE_BASED",
  COMMERCIAL_MARKET: "RENT_REGULATION_COMMERCIAL_MARKET",
  SOCIAL_BALANCE: "RENT_REGULATION_SOCIAL_BALANCE",
} as const;

export const getRentRegulationTranslationKey = (rentRegulation?: Nullable<string>): TranslationKey =>
  RENT_REGULATION_TRANSLATION_KEYS[rentRegulation ?? "UNKNOWN"] ?? "RENT_REGULATION_UNKNOWN";

export const getTenancyTypeDistribution = (tenancies: Tenancy[]): { [key: string]: { value: number; translationKey: TranslationKey } } => {
  const groups = groupBy(tenancies, { tenancyType: { getKey: (tenancy) => tenancy.tenancyType ?? "UNKNOWN", type: "string" } });

  const dist: { [key: string]: { value: number; translationKey: TranslationKey } } = {};

  for (let [tenancyType, group] of groups.map) {
    const translationKey = getTenancyTypeTranslationKey(group.tenancyType);

    dist[tenancyType] = {
      value: group.items.length,
      translationKey,
    };
  }

  return dist;
};

export const getTenancyTypeDistributionPrSquareMeter = (tenancies: Tenancy[]) => {
  const areaUnit: Nullable<string> = tenancies.find((tenancy) => !!tenancy.areaUnit)?.areaUnit;

  const groups = groupBy(tenancies, {
    tenancyType: { getKey: (tenancy) => tenancy.tenancyType ?? "UNKNOWN", type: "string" },
  });

  const distribution: { [key: string]: { value: number; translationKey: TranslationKey } } = {};

  for (let [tenancyType, group] of groups.map) {
    const translationKey = getTenancyTypeTranslationKey(group.tenancyType);

    distribution[tenancyType] = {
      value: sum(group.items.map((tenancy) => convertArea(tenancy.area ?? 0, tenancy.areaUnit, areaUnit))),
      translationKey,
    };
  }

  return { distribution, areaUnit };
};

export const getDefaultTenancyColumnSettings = () => TenanciesUserPreferenceColumns.filter((header) => header.enabled).map((header) => header.key);
